import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// Interfaces para los DTOs de Wompi Reports

export interface WompiReportDTO {
  transactionId: string;
  transactionDate: string;
  reference: string;
  amount: number;
  vat: number;
  consumptionTax: number;
  currency: string;
  paymentMethod: string;
  payerEmail: string;
  payerName: string;
  payerPhone: string;
  reconciliationId: string;
  paymentLinkId: string;
  payerDocument: string;
  payerDocumentType: string;
  ref1Name: string;
  ref1Value: string;
  ref2Name: string;
  ref2Value: string;
}

export interface PlinkReportDTO {
  nit: string;
  establishmentCode: string;
  purchaseOrigin: string;
  transactionType: string;
  franchise: string;
  networkIdentifier: string;
  transactionDate: string;
  redemptionDate: string;
  depositAccount: string;
  purchaseValue: number;
  tipValue: number;
  ivaValue: number;
  ipoConsumoValue: number;
  totalValue: number;
  commissionValue: number;
  reteFuenteValue: number;
  reteIVAValue: number;
  reteICAValue: number;
  provisionValue: number;
  netValue: number;
  authorizationCode: string;
  cardType: string;
  terminalNumber: string;
  cardNumber: string;
  porcentualCommission: number;
  baseCommission: number;
  compensationDate: string;
}

export interface WompiReportPreviewDTO {
  records: WompiReportDTO[];
  totalRecords: number;
  fileName: string;
  fileSize: number;
  hasErrors: boolean;
  errors: string[];
  duplicatesCount: number;
}

export interface PlinkReportPreviewDTO {
  records: PlinkReportDTO[];
  totalRecords: number;
  fileName: string;
  fileSize: number;
  hasErrors: boolean;
  errors: string[];
  duplicatesCount: number;
}

export interface ReportUploadBatchDTO {
  id: number;
  uploadDate: string;
  uploadedBy: string;
  wompiReportCount: number;
  plinkReportCount: number;
  isComplete: boolean;
  notes?: string;
  wompiFileName?: string;
  plinkFileName?: string;
}

export interface ReportUploadResultDTO {
  batchId: number;
  recordsProcessed: number;
  duplicatesIgnored: number;
  success: boolean;
  message: string;
  errors: string[];
}

export interface MultipleUploadResponseDTO {
  jobId: string;
  filesQueued: number;
  message: string;
  fileNames: string[];
}

export interface DailyTaskStatusDTO {
  taskDate: string;
  wompiUploaded: boolean;
  wompiUploadedAt?: string;
  plinkUploaded: boolean;
  plinkUploadedAt?: string;
  isComplete: boolean;
  completedAt?: string;
  wompiBatchId?: number;
  plinkBatchId?: number;
}

export interface ReportConciliationLogDTO {
  id: number;
  executionDate: string;
  totalProcessed: number;
  totalConciliated: number;
  totalFailed: number;
  totalPending: number;
  status: number;
  statusName: string;
  errorDetails?: string;
  executionTimeMs: number;
  conciliatedPaymentsSummary?: string;
  emailNotificationSent: boolean;
  emailSentAt?: string;
}

export interface ConciliationConfigDTO {
  emails: string[];
  enabled: boolean;
}

export interface WompiReportSummaryDTO {
  id: number;
  transactionId: string;
  transactionDate: string;
  reference: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  payerName: string;
  payerEmail: string;
  reconciliationId: string;
  paymentLinkId: string;
  conciliated: boolean;
  conciliatedAt?: string;
  uploadBatchId?: number;
  createdAt?: string;
  enrollmentId?: number;
  incomeReceiptId?: number;
  paymentPlanId?: number;
  systemStatus?: string;
  isAutoConciliable?: boolean;
}

export interface PlinkReportSummaryDTO {
  id: number;
  authorizationCode: string;
  transactionDate: string;
  redemptionDate: string;
  franchise: string;
  totalValue: number;
  commissionValue: number;
  reteFuenteValue: number;
  reteIVAValue: number;
  reteICAValue: number;
  provisionValue: number;
  netValue: number;
  cardType: string;
  conciliated: boolean;
  conciliatedAt?: string;
  uploadBatchId?: number;
  createdAt?: string;
}

export interface UploadedReportsResponseDTO {
  wompiReports: WompiReportSummaryDTO[];
  plinkReports: PlinkReportSummaryDTO[];
  totalWompiReports: number;
  totalPlinkReports: number;
  totalConciliatedWompi: number;
  totalConciliatedPlink: number;
  totalPendingWompi: number;
  totalPendingPlink: number;
}

@Injectable({
  providedIn: 'root'
})
export class WompiReportsService {
  private readonly apiUrl = `${environment.apiUrl}/WompiReports`;

  constructor(private http: HttpClient) { }

  // ============== Preview Methods ==============

  /**
   * Obtiene una vista previa del archivo CSV de Wompi sin guardarlo
   */
  previewWompiReport(file: File): Observable<WompiReportPreviewDTO> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<WompiReportPreviewDTO>(`${this.apiUrl}/preview-wompi`, formData);
  }

  /**
   * Obtiene una vista previa del archivo Excel de Plink sin guardarlo
   */
  previewPlinkReport(file: File): Observable<PlinkReportPreviewDTO> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<PlinkReportPreviewDTO>(`${this.apiUrl}/preview-plink`, formData);
  }

  // ============== Upload Methods ==============

  /**
   * Sube y procesa el archivo CSV de Wompi
   */
  uploadWompiReport(file: File): Observable<ReportUploadResultDTO> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ReportUploadResultDTO>(`${this.apiUrl}/upload-wompi`, formData);
  }

  /**
   * Sube múltiples archivos CSV de Wompi para procesamiento asíncrono en background.
   * Se notificará por email cuando el procesamiento finalice.
   */
  uploadWompiReportMultiple(files: File[]): Observable<MultipleUploadResponseDTO> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return this.http.post<MultipleUploadResponseDTO>(`${this.apiUrl}/upload-wompi-multiple`, formData);
  }

  /**
   * Sube y procesa el archivo Excel de Plink
   */
  uploadPlinkReport(file: File): Observable<ReportUploadResultDTO> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ReportUploadResultDTO>(`${this.apiUrl}/upload-plink`, formData);
  }

  /**
   * Sube múltiples archivos Excel de Plink para procesamiento asíncrono en background.
   * Se notificará por email cuando el procesamiento finalice.
   */
  uploadPlinkReportMultiple(files: File[]): Observable<MultipleUploadResponseDTO> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return this.http.post<MultipleUploadResponseDTO>(`${this.apiUrl}/upload-plink-multiple`, formData);
  }

  // ============== Batch Methods ==============

  /**
   * Obtiene la lista de batches de carga
   */
  getBatches(from?: Date, to?: Date): Observable<ReportUploadBatchDTO[]> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    return this.http.get<ReportUploadBatchDTO[]>(`${this.apiUrl}/batches`, { params });
  }

  /**
   * Obtiene un batch específico por ID
   */
  getBatchById(id: number): Observable<ReportUploadBatchDTO> {
    return this.http.get<ReportUploadBatchDTO>(`${this.apiUrl}/batches/${id}`);
  }

  // ============== Daily Task Methods ==============

  /**
   * Obtiene el estado de la tarea diaria del usuario actual
   */
  getDailyTaskStatus(): Observable<DailyTaskStatusDTO> {
    return this.http.get<DailyTaskStatusDTO>(`${this.apiUrl}/daily-task-status`);
  }

  /**
   * Obtiene el historial de tareas diarias
   */
  getDailyTaskHistory(days: number = 30): Observable<DailyTaskStatusDTO[]> {
    const params = new HttpParams().set('days', days.toString());
    return this.http.get<DailyTaskStatusDTO[]>(`${this.apiUrl}/daily-task-history`, { params });
  }

  // ============== Conciliation Methods ==============

  /**
   * Obtiene los logs de conciliación
   */
  getConciliationLogs(from?: Date, to?: Date): Observable<ReportConciliationLogDTO[]> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    return this.http.get<ReportConciliationLogDTO[]>(`${this.apiUrl}/conciliation-logs`, { params });
  }

  /**
   * Obtiene un log de conciliación específico
   */
  getConciliationLogById(id: number): Observable<ReportConciliationLogDTO> {
    return this.http.get<ReportConciliationLogDTO>(`${this.apiUrl}/conciliation-logs/${id}`);
  }

  /**
   * Ejecuta la conciliación manualmente (requiere permisos de administrador)
   */
  runConciliation(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/run-conciliation`, {});
  }

  // ============== Configuration Methods ==============

  /**
   * Obtiene los emails configurados para notificación
   */
  getNotificationEmails(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/conciliation-config/emails`);
  }

  /**
   * Actualiza los emails de notificación
   */
  updateNotificationEmails(emails: string[]): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/conciliation-config/emails`, emails);
  }

  /**
   * Verifica si la conciliación automática está habilitada
   */
  isConciliationEnabled(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/conciliation-config/enabled`);
  }

  /**
   * Habilita o deshabilita la conciliación automática
   */
  setConciliationEnabled(enabled: boolean): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/conciliation-config/enabled`, enabled);
  }

  // ============== Uploaded Reports Methods ==============

  /**
   * Obtiene los reportes cargados con filtros
   */
  getUploadedReports(
    from?: Date,
    to?: Date,
    conciliated?: boolean,
    linked?: boolean,
    page: number = 1,
    pageSize: number = 50
  ): Observable<UploadedReportsResponseDTO> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    if (conciliated !== undefined) {
      params = params.set('conciliated', conciliated.toString());
    }
    if (linked !== undefined) {
      params = params.set('linked', linked.toString());
    }
    return this.http.get<UploadedReportsResponseDTO>(`${this.apiUrl}/uploaded-reports`, { params });
  }

  /**
   * Obtiene solo los reportes Wompi con filtros
   */
  getWompiReports(
    from?: Date,
    to?: Date,
    conciliated?: boolean,
    linked?: boolean,
    page: number = 1,
    pageSize: number = 50
  ): Observable<WompiReportSummaryDTO[]> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    if (conciliated !== undefined) {
      params = params.set('conciliated', conciliated.toString());
    }
    if (linked !== undefined) {
      params = params.set('linked', linked.toString());
    }
    return this.http.get<WompiReportSummaryDTO[]>(`${this.apiUrl}/wompi-reports`, { params });
  }

  /**
   * Obtiene solo los reportes Plink con filtros
   */
  getPlinkReports(
    from?: Date,
    to?: Date,
    conciliated?: boolean,
    page: number = 1,
    pageSize: number = 50
  ): Observable<PlinkReportSummaryDTO[]> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    if (conciliated !== undefined) {
      params = params.set('conciliated', conciliated.toString());
    }
    return this.http.get<PlinkReportSummaryDTO[]>(`${this.apiUrl}/plink-reports`, { params });
  }

  /**
   * Genera y descarga el reporte consolidado en Excel
   */
  getConsolidatedExcelReport(from: Date, to: Date): Observable<Blob> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', from.toISOString());
    }
    if (to) {
      params = params.set('to', to.toISOString());
    }
    return this.http.get(`${this.apiUrl}/excel-consolidated`, {
      params,
      responseType: 'blob'
    });
  }
}

